import Input from '@/shared/components/rebranded/Input/Input';
import { useTranslations } from '@storebrand-digital/fms-translations-react';
import { Label } from '../../shared/components/Label/Label';
import { FieldProps, IntegerSchema } from '../types';
import { joinErrors } from '../validation/utils';
import { hasErrors } from '../validation/validateSchema';

export type IntegerInputFieldProps = FieldProps<IntegerSchema> & {
  suffix?: string;
  suffixAriaLabel?: string;
};

export const IntegerInputField = ({
  schema,
  name: fieldName,
  value: fieldValue,
  touched,
  errors,
  id,
  onChange,
  forceShowError,
  readOnly,
  suffix = '',
  suffixAriaLabel
}: IntegerInputFieldProps) => {
  const { getText } = useTranslations();
  const $uiOptions = schema.$uiOptions || {};
  const showError = !!((touched || forceShowError) && hasErrors(errors));

  const handleChange = (e: any) => {
    const { name } = e.target;
    const value = toNumber(e.target.value);

    onChange({
      target: {
        name,
        value
      }
    });
  };

  const minimum = 0;
  const maximum = Number.MAX_SAFE_INTEGER;
  const useDefaultSize = !$uiOptions.fieldSize;

  const handleKeyDown = (e: any) => {
    const { name } = e.target;

    switch (e.key) {
      case 'ArrowDown': {
        onChange({
          target: {
            name,
            value: Math.max(+fieldValue! - 1, minimum)
          }
        });
        break;
      }

      case 'ArrowUp': {
        onChange({
          target: {
            name,
            value: Math.min(+fieldValue! + 1, maximum)
          }
        });
        break;
      }

      default:
    }
  };

  const error = showError ? joinErrors(errors, getText) : undefined;

  const label = (
    <Label title={schema.title} description={schema.description} id={id} />
  );

  const formattedFieldValue =
    fieldValue == null || (fieldValue as any) === ''
      ? ''
      : (fieldValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ') as any);

  return (
    <Input
      id={id}
      inputProps={{
        inputMode: 'numeric',
        pattern: '[0-9]*',
        'aria-describedby': schema.description
          ? `${id}_description`
          : undefined,
        autoFocus: $uiOptions.autoFocus,
        minLength: 0,
        maxLength: 15
      }}
      name={fieldName}
      label={label}
      hint={$uiOptions.hint}
      placeholder={$uiOptions.placeholder}
      error={error}
      small={useDefaultSize}
      medium={$uiOptions.fieldSize === 'medium'}
      value={formattedFieldValue}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      disabled={readOnly}
      suffix={$uiOptions.suffix ?? suffix}
      suffixProps={{
        'aria-label': $uiOptions.suffixAriaLabel ?? suffixAriaLabel
      }}
      type="text"
    />
  );
};

const toNumber = (value: string) => {
  if (value == null) {
    return null;
  }

  let filtered = value;

  // If the user paste 1300,50 we strip it
  // so it becomes 1300 instead of 130050;
  const commaIndex = value.lastIndexOf(',');

  if (commaIndex > -1) {
    filtered = filtered.slice(0, commaIndex);
  }

  filtered = filtered.replace(/\D+/g, '');

  if (filtered.trim() === '') {
    return null;
  }

  // Avoid large numbers
  const capped = filtered.slice(0, 15);

  return parseInt(capped, 10);
};
